




































































































































































































import {Component, Prop, Vue} from "vue-property-decorator";
import {Route} from "vue-router";
import InputPageWrapper from "@/views/components/input/InputPageWrapper.vue";

import {designStore} from "@/store/store";
import {UnitSystem} from "@/interfaces/units";
import PxNumberInput from "@/views/components/input/formFields/PxNumberInput.vue";
import GenericPavementDiagram from "@/modules/design/views/components/GenericPavementDiagram.vue";
import {IDesignFlexibleSubstructureLayer} from "@/modules/design/interfaces/shared";
import {getAsphaltLayers} from "@/modules/design/lib/getLayers";
import QuickHelp from "@/views/components/input/QuickHelp.vue";
import {INCHES_TO_CM, PSI_TO_MPA} from "@/lib/unitConversion/unitConversionFactors";
import {createAsphaltValidations} from "@/modules/design/lib/validations/asphalt";
import ValidatedInputPageWrapper from "@/views/components/input/ValidatedInputPageWrapper.vue";


@Component({
  components: {
    ValidatedInputPageWrapper,
    QuickHelp,
    GenericPavementDiagram,
    InputPageWrapper,
    PxNumberInput,
  },
})
export default class NewAsphaltSubstructure extends Vue {
  @Prop() readonly pxDesignId!: string;

  @Prop() readonly page!: number;

  get scenario() {
    return designStore.currentAsphalt;
  }

  get scenarioImperial() {
    return designStore.currentAsphaltImperial;
  }

  validations() {
    // @ts-ignore
    return createAsphaltValidations(this.scenarioImperial);
  }

  getBaseLayerValidators(layer: number) {
    // This function was added because when new layers are generated the validations don't update, which was blocking
    // display of the table and throwing errors.
    // Solution: find a way to force recomputation of validations()
    // @ts-ignore
    return this.$v.scenarioImperial.substructureLayers[layer] ? this.$v.scenarioImperial.substructureLayers[layer] : {
      coefficient: null,
      modulus: null,
      thickness: null,
      drainage: null,
    };
    // return this.$v.scenarioImperial.substructureLayers[layer];
  }

  layerTypeOptions = [
    {value: null, text: "Type", coefficient: 0, modulus_psi: 0},
    {value: "aggregate", text: "Aggregate Base", coefficient: 0.14, modulus_psi: 28000},
    {value: "bituminousTreated", text: "Bituminous treated base", coefficient: 0.3, modulus_psi: 28000},
    {value: "cementOrLimeTreated", text: "Cement or lime treated base", coefficient: 0.23, modulus_psi: 28000},
    {value: "aggregateSubbase", text: "Aggregate subbase", coefficient: 0.11, modulus_psi: 28000},
  ];

  updateLayer(layer: IDesignFlexibleSubstructureLayer) {
    const options = this.layerTypeOptions.filter((LTO) => LTO.value === layer.type)[0];

    // Get imperial version of this layer
    const layerToEdit = designStore.currentAsphaltImperial.substructureLayers.filter((L) => L.id === layer.id)[0];

    layerToEdit.coefficient = options.coefficient;
    layerToEdit.modulus = options.modulus_psi;
    layerToEdit.name = options.text;

    this.sync();
  }

  deleteLayer(layer: IDesignFlexibleSubstructureLayer) {
    designStore.currentAsphaltImperial.substructureLayers = designStore.currentAsphaltImperial.substructureLayers.filter(
        (L) => L.id !== layer.id,
    );
    this.sync();
  }

  get layers() {
    return getAsphaltLayers(designStore.currentAsphaltImperial, this.thicknessUnit);
  }

  addSubstructureLayer() {
    if (this.scenario.unitSystem === UnitSystem.Imperial) {
      const newLayerImperial: IDesignFlexibleSubstructureLayer = {
        // length: number,
        id: null,
        coefficient: 0,
        drainage: 1,
        thickness: 4, // inches
        name: "Substructure",
        type: null,
        modulus: 28000, // PSI
        fixedThickness: false,
      };
      designStore.currentAsphaltImperial.substructureLayers.push(newLayerImperial);
    } else {
      const newLayerMetric: IDesignFlexibleSubstructureLayer = {
        // length: number,
        id: null,
        coefficient: 0,
        drainage: 1,
        thickness: 4 * INCHES_TO_CM, // inches
        name: "Substructure",
        type: null,
        modulus: 28000 * PSI_TO_MPA, // PSI
        fixedThickness: false,
      };
      designStore.currentAsphalt.substructureLayers.push(newLayerMetric);
    }

    this.sync();
  }


  sync() {
    designStore.setCurrentAsphaltScenario(this.scenario);
  }

  save() {
    this.sync();
    designStore.saveCurrentAsphaltScenario();
  }

  beforeRouteLeave(to: Route, from: Route, next: Function) {
    this.save();
    next();
  }

  fields = [
    {
      key: "type",
      label: "Layer Type",
    },
    {
      key: "coefficient",
      label: "Layer Coeff.",
    },
    {
      key: "drainage",
      label: "Drainage Coeff.",
    },
    {
      key: "thickness",
      label: "Thickness",
    },
    {
      key: "modulus",
      label: "Resilient Modulus",
    },
    {
      key: "fixedThickness",
      label: "Fixed"
    },
    {
      key: "Action",
      label: "",
    },
  ];

  get thicknessUnit() {
    return this.unitSystemIsImperial ? "in" : "cm";
  }

  get designSubgradeModulusUnit() {
    return this.unitSystemIsImperial ? "PSI" : "MPa";
  }

  get unitSystemIsImperial() {
    return this.scenario.displayUnits === UnitSystem.Imperial;
  }

  /*
   Modulus calculation tooling
   */

  modulusCalculationMethod: "R-Value" | "CBR" | null = null;

  modulusCalculationMethodOptions = [
    {value: "R-Value", text: "R-Value"},
    {value: "CBR", text: "CBR"},
    {value: null, text: "Manual"},
  ];

  get modulusCalculationHelptext(): string | null {
    switch (this.scenario.modulusCalculationMethod) {
      case "R-Value":
        return "<strong>R-Value Calculation:</strong> MR = 1,000 + (555)(R-value)";
      case "CBR":
        return "<strong>CBR Calculation:</strong> MR = 2555 x CBR^0.64";
    }
    return "Please determine the subgrade modulus and enter in the Modulus field.";
  }

  calcModulusFromRValue() {
    // @ts-ignore
    this.scenarioImperial.subgrade = Number((1000 + 555 * this.scenarioImperial.rvalue).toFixed(2));
    return true; // Return true to play nice with getting called from "&& doStuff()" in v-in
  }

  calcModulusFromCbr() {
    // @ts-ignore
    this.scenarioImperial.subgrade = Number((2555 * Math.pow(this.scenarioImperial.cbr, 0.64)).toFixed(2));
    return true; // Return true to play nice with getting called from "&& doStuff()" in v-in
  }


  // AASHTO Soil model
  subgradeSoilType: string | null = null;

  subgradeAashtoModulusBounds = {
    "A-1-a": [38500, 42000],
    "A-1-b": [35500, 40000],
    "A-2-4": [28000, 37500],
    "A-2-5": [24000, 33000],
    "A-2-6": [21500, 31000],
    "A-2-7": [21500, 28000],
    "A-3": [24500, 35500],
    "A-4": [21500, 29000],
    "A-5": [17000, 25500],
    "A-6": [13500, 24000],
    "A-7-5": [8000, 17500],
    "A-7-6": [5000, 13500],
  };

  get modulusBoundsMultiplier() {
    return this.unitSystemIsImperial ? 1 : PSI_TO_MPA;
  }

  get subgradeSoilTypeOptions() {
    const soilTypes = Object.keys(this.subgradeAashtoModulusBounds).map((k): { value: string | null, text: string } => ({
      value: k,
      text: k,
    }));
    soilTypes.unshift({value: null, text: "---------"}); // Prepend default option
    return soilTypes;
  }
}

